import { ColorPicker, Input, Switch, Select } from "antd";
import css from "./ClientBuilderPanel.module.css";
import {
    LANGUAGE_CODE_LABEL,
    TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE,
    getEditableTranslatableValue,
} from "./translatable.util";
import { CardsEditor } from "./CardsEditor";
import { ImagePickerField } from "./ImagePickerField";
import { RichTextEditorField } from "./RichTextEditorField";
import { WRAP_OPTIONS, resolveWrapDefault } from "./wrapControl.constants";
import { buildSectionAnchorOptions } from "./sectionAnchors.constants";

// Renders one top-level schema field's editor row — extracted verbatim from
// ClientBuilderPanel.js's schema.map() callback (same field-type branches,
// same JSX, same behavior), so a new field type or a tweak to an existing
// one has exactly one place to change instead of being buried inside the
// panel's giant render. `cards`/`imagePicker` types render standalone (no
// shared propRow wrapper, matching the original's early returns); every
// other type shares one propRow with a label.
function SchemaFieldControl({
    field,
    props,
    selectedLanguage,
    selectedKey,
    selectedLayout,
    onSetProp,
    onSetTranslatableProp,
}) {
    const { key, label, type, options, fields, newItemDefault, translatable, previewPaths, wrapControl, groupBy, nonDeletableIds, cardLabel, autoStyleKey, autoStyleMap, allowAdd, restoreCandidatesByLayout } = field;

    if (type === "cards") {
        // `sectionAnchor` fields (currently just navbar's navLinks.href) are
        // defined layout-agnostically in their constants file — the actual
        // dropdown options depend on WHICH layout is being edited (each
        // brand's own hideable sections), so they're resolved here, at
        // render time, using `selectedLayout`. Every other field type
        // passes through unchanged.
        const resolvedFields = (fields || []).map((f) =>
            f.type === "sectionAnchor"
                ? { ...f, options: buildSectionAnchorOptions(selectedLayout) }
                : f
        );
        // Whichever of this layout's default items (e.g. navbar's
        // navLinks) aren't currently present — matched by `href`, the
        // stable identifier — become "+ Restore X" buttons in CardsEditor.
        // Layout-specific (Meelz's default set has 5 items, CK/E4F have 4)
        // purely because `restoreCandidatesByLayout` already is; nothing
        // here hardcodes a count.
        const currentItems = props[key] || [];
        const restoreCandidates = (restoreCandidatesByLayout?.[selectedLayout] || []).filter(
            (candidate) => !currentItems.some((item) => item.href === candidate.href)
        );
        return (
            <div style={{ padding: "8px 0 4px" }}>
                <span className={css.propLabel} style={{ display: "block", padding: "0 16px 6px" }}>{label}</span>
                <CardsEditor
                    // Remounts (resetting internal `expanded` state) whenever the
                    // selected component or layout changes — otherwise switching
                    // layouts within the same component (same schema field `key`,
                    // e.g. hero's "features") carries over a stale expanded-card
                    // index into a completely different card list.
                    key={`${selectedKey}-${selectedLayout}-${key}`}
                    cards={currentItems}
                    onChange={(next) => onSetProp(key, next)}
                    fields={resolvedFields}
                    newItemDefault={newItemDefault}
                    selectedLanguage={selectedLanguage}
                    componentKey={selectedKey}
                    groupBy={groupBy}
                    nonDeletableIds={nonDeletableIds}
                    cardLabel={cardLabel}
                    allowAdd={allowAdd}
                    restoreCandidates={restoreCandidatesByLayout ? restoreCandidates : undefined}
                    onRestore={(candidate) => onSetProp(key, [...currentItems, candidate])}
                />
            </div>
        );
    }
    // sectionOrderList (sectionOrder's `order` field) is NOT handled here —
    // its Active/Shown toggles per row need cross-entry propsMap access
    // (another registry entry's own active/visible, not just this entry's
    // own props) that this component doesn't have. It's special-cased
    // directly in ClientBuilderPanel.js's schema.map() loop instead, ahead
    // of this component ever being called for that field type.
    if (type === "imagePicker") {
        return (
            <ImagePickerField
                label={label}
                options={options}
                source={props[`${key}Source`]}
                value={props[key]}
                onSourceChange={(v) => onSetProp(`${key}Source`, v)}
                onValueChange={(v) => {
                    onSetProp(key, v);
                    // Some image assets have exactly one correct companion
                    // style (hero's image -> imageStyle: a ~99%-transparent
                    // product cutout rendered with "boxed"'s object-cover
                    // crop + gradient overlay looks visibly broken/cluttered
                    // — confirmed live bug). Auto-corrects the companion
                    // field to match the newly picked image, same as
                    // logoSource auto-following an upload/library choice
                    // elsewhere in this file. No-op for any field without
                    // autoStyleKey/autoStyleMap (e.g. navbar's logo).
                    if (autoStyleKey && autoStyleMap && autoStyleMap[v] !== undefined) {
                        onSetProp(autoStyleKey, autoStyleMap[v]);
                    }
                }}
                libraryPreviewPaths={previewPaths}
            />
        );
    }
    // richText renders standalone (full width), same reasoning as
    // cards/imagePicker above — a Tiptap toolbar+editor doesn't fit the
    // shared 150px-wide propRow every other type uses. Translation wiring
    // is identical to type:"text" (same selectedLanguage/getEditableTranslatableValue/
    // onSetTranslatableProp trio) — only the input widget differs.
    if (type === "richText") {
        return (
            <div style={{ padding: "8px 0 4px" }}>
                <span className={css.propLabel} style={{ display: "block", padding: "0 16px 6px" }}>
                    {label}
                    {translatable && ` (${LANGUAGE_CODE_LABEL[selectedLanguage]})`}
                </span>
                <div style={{ padding: "0 16px" }}>
                    <RichTextEditorField
                        key={`${selectedKey}-${selectedLayout}-${key}-${selectedLanguage}`}
                        value={translatable ? getEditableTranslatableValue(props[key], selectedLanguage) : props[key]}
                        onChange={(html) => (translatable
                            ? onSetTranslatableProp(key, html)
                            : onSetProp(key, html))}
                        dir={translatable && selectedLanguage === "ar" ? "rtl" : "ltr"}
                    />
                </div>
            </div>
        );
    }
    return (
        <div className={css.propRow}>
            <span className={css.propLabel}>
                {label}
                {translatable && ` (${LANGUAGE_CODE_LABEL[selectedLanguage]})`}
            </span>
            {type === "color" && (
                <div className={css.propColorWrap}>
                    <code className={css.propHex}>{props[key]}</code>
                    <ColorPicker
                        value={props[key]}
                        onChange={(v) => onSetProp(key, v.toHexString())}
                        size="small"
                    />
                </div>
            )}
            {type === "text" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                    <Input
                        value={translatable ? getEditableTranslatableValue(props[key], selectedLanguage) : props[key]}
                        onChange={(e) => (translatable
                            ? onSetTranslatableProp(key, e.target.value)
                            : onSetProp(key, e.target.value))}
                        placeholder={translatable
                            ? TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE[selectedLanguage](label)
                            : undefined}
                        size="small"
                        style={{ width: 150 }}
                        dir={translatable && selectedLanguage === "ar" ? "rtl" : "ltr"}
                    />
                    {wrapControl && (
                        <Select
                            value={props[`${key}Wrap`] ?? resolveWrapDefault(selectedKey, key)}
                            onChange={(v) => onSetProp(`${key}Wrap`, v)}
                            options={WRAP_OPTIONS}
                            size="small"
                            style={{ width: 150 }}
                        />
                    )}
                </div>
            )}
            {type === "boolean" && (
                <Switch
                    checked={props[key]}
                    onChange={(v) => onSetProp(key, v)}
                    size="small"
                />
            )}
            {type === "select" && (
                <Select
                    value={props[key]}
                    onChange={(v) => onSetProp(key, v)}
                    options={options}
                    size="small"
                    style={{ width: 150 }}
                />
            )}
        </div>
    );
}

export { SchemaFieldControl };
